import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { parseISO } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import axios from "axios";
import {
  CheckCircle,
  XCircle,
  Calendar,
  Loader2,
  AlertCircle,
  Palmtree,
} from "lucide-react";

import { API } from "@/lib/api";

const ApprovalLinkPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [requestData, setRequestData] = useState(null);

  const requestId = searchParams.get("request_id");
  const token = searchParams.get("token");
  const accion = searchParams.get("accion"); // 'aprobar' | 'rechazar'

  useEffect(() => {
    const processApproval = async () => {
      if (!requestId || !token || !accion) {
        setError("Enlace inválido. Faltan parámetros requeridos.");
        setLoading(false);
        return;
      }

      try {
        // First, get request details using public endpoint
        const detailResponse = await axios.get(`${API}/vacaciones/public/${requestId}`, {
          params: { token }
        });
        const currentData = detailResponse.data;
        setRequestData(currentData);

        // If it's already processed, just show the result
        if (currentData.estado !== "pendiente") {
          setResult({
            message: `Esta solicitud ya fue ${currentData.estado === 'aprobado' ? 'aprobada' : 'rechazada'}.`,
            estado: currentData.estado
          });
          setLoading(false);
          return;
        }

        // Only process the approval/rejection if it's still pending
        const response = await axios.get(
          `${API}/vacaciones/aprobar-email/${requestId}`,
          { params: { token, accion } }
        );

        setResult(response.data);
      } catch (err) {
        console.error("Error processing approval:", err);
        setError(
          err.response?.data?.detail ||
            "Error al procesar la solicitud. El enlace puede haber expirado."
        );
      } finally {
        setLoading(false);
      }
    };

    processApproval();
  }, [requestId, token, accion]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return parseISO(dateString).toLocaleDateString("es-GT", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/10 p-1.5 bg-white border border-slate-100">
            <img src="/logo-cps.png" alt="CPS Logistics" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">CPS Time Off</h1>
            <p className="text-sm text-slate-500">Gestión de Vacaciones</p>
          </div>
        </div>

        <Card className="border-0 shadow-xl shadow-slate-200/50">
          <CardHeader className="text-center">
            <CardTitle>
              {loading
                ? "Procesando solicitud..."
                : error
                ? "Error"
                : accion === "aprobar"
                ? "Aprobación de Vacaciones"
                : "Rechazo de Vacaciones"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {loading ? (
              <div className="flex flex-col items-center py-8">
                <Loader2 className="w-12 h-12 text-orange-500 animate-spin mb-4" />
                <p className="text-slate-600">Procesando tu solicitud...</p>
              </div>
            ) : error ? (
              <div className="space-y-4">
                <Alert variant="destructive" className="bg-red-50 border-red-200">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/login")}
                >
                  Ir al Sistema
                </Button>
              </div>
            ) : result ? (
              <div className="space-y-6">
                {/* Success Message */}
                <div
                  className={`flex flex-col items-center py-6 rounded-lg ${
                    result.estado === "aprobado"
                      ? "bg-emerald-50"
                      : "bg-red-50"
                  }`}
                >
                  {result.estado === "aprobado" ? (
                    <CheckCircle className="w-16 h-16 text-emerald-500 mb-4" />
                  ) : (
                    <XCircle className="w-16 h-16 text-red-500 mb-4" />
                  )}
                  <p
                    className={`text-lg font-semibold ${
                      result.estado === "aprobado"
                        ? "text-emerald-800"
                        : "text-red-800"
                    }`}
                  >
                    {result.message}
                  </p>
                </div>

                {/* Request Details */}
                {requestData && (
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Colaborador</span>
                      <span className="font-medium text-slate-800">
                        {requestData.empleado_nombre}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Período</span>
                      <span className="text-slate-800">
                        {formatDate(requestData.fecha_inicio)} -{" "}
                        {formatDate(requestData.fecha_fin)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Días</span>
                      <Badge variant="outline" className="bg-sky-50 text-sky-700">
                        {requestData.dias_solicitados} días
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Estado</span>
                      <Badge
                        variant="outline"
                        className={
                          result.estado === "aprobado"
                            ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                            : "bg-red-100 text-red-700 border-red-200"
                        }
                      >
                        {result.estado === "aprobado" ? "Aprobada" : "Rechazada"}
                      </Badge>
                    </div>
                  </div>
                )}

                <p className="text-center text-sm text-slate-500">
                  El colaborador recibirá una notificación sobre esta decisión.
                </p>

                <Button
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => navigate("/login")}
                >
                  Ir al Sistema
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ApprovalLinkPage;
